import React, { useEffect, useState } from "react";
import { useNavigate,  useParams } from "react-router-dom";
import moment from "moment";
import requests from "../../axios/requests";

import { DemoRow } from "../GroupsList/GroupsList";
import PrevBtn from "../../components/reuseComponents/PrevBtn";
import variableLanguage from "../../utils/hooks/hookGetVariablesLanguage";

import { ReactComponent as Accept } from "../../../images/icons/result_accept.svg";
import { ReactComponent as Deny } from "../../../images/icons/result_false.svg";
import fake_photos from "../../../images/icons/fake_photos.png";
import ImageLightbox from "../../components/reuseComponents/ImageLightbox";

// Опциональная миниатюра ответа - доступна у любого типа вопроса, не только у
// бывшего отдельного типа "выбор изображения". Клик открывает полный размер
// (без обрезки) в ImageLightbox.
const AnswerThumb = ({answer, onImageClick}) => {
    if (!answer.img_question) return null;
    return (
        <img
            src={answer.img_question}
            alt=""
            className="quiz_answer_image_thumb_result"
            onClick={(e) => {
                e.stopPropagation();
                onImageClick(answer.img_question_full || answer.img_question);
            }}
        />
    );
};

// Результаты по вопросу типа "один/несколько вариантов" - подсветка
// (выбран+верно / выбран+неверно / не выбран+верно (эталон) / нейтрально). Та же
// логика, что и на итоговом экране тренировочного теста ученика (Quiz.jsx).
const renderChoiceResult = (question, onImageClick) => {
    const submitted = question.submitted_answers || [];
    const correctIds = question.correct_answers_ids || [];
    const items = (question.answers || []).map((answer) => {
        const selected = submitted.includes(answer.id);
        const isCorrectAnswer = correctIds.includes(answer.id);
        let state = "neutral";
        if (selected && isCorrectAnswer) state = "correct";
        else if (selected && !isCorrectAnswer) state = "wrong";
        else if (!selected && isCorrectAnswer) state = "missed";
        return {answer, state};
    });
    // Крупные карточки (образ "Выбор изображения"/CAPTCHA) - только когда у
    // вопроса реально есть картинки, иначе обычный компактный текстовый список.
    const hasImages = (question.answers || []).some((a) => a.img_question);
    if (hasImages) {
        return (
            <div className="result_choice_list result_choice_list--cards">
                {items.map(({answer, state}) => (
                    <div key={answer.id} className={`result_choice_card ${state}`}>
                        <div className="result_choice_card_media">
                            {answer.img_question &&
                                <img
                                    src={answer.img_question}
                                    alt=""
                                    onClick={() => onImageClick(answer.img_question_full || answer.img_question)}
                                />}
                            <span className="result_icon">
                                {state === "correct" && "✔"}
                                {state === "wrong" && "✕"}
                            </span>
                        </div>
                        <div className="result_choice_card_text">{answer.text}</div>
                    </div>
                ))}
            </div>
        );
    }
    return (
        <div className="result_choice_list">
            {items.map(({answer, state}) => (
                <div key={answer.id} className={`result_choice_item ${state}`}>
                    <span className="result_icon">
                        {state === "correct" && "✔"}
                        {state === "wrong" && "✕"}
                    </span>
                    {answer.text}
                </div>
            ))}
        </div>
    );
};

// Установление последовательности: сравниваем позицию за позицией отправленный
// студентом порядок с правильным, плюс отдельный ряд с правильным порядком, если
// есть ошибки.
const renderOrderingResult = (question, onImageClick) => {
    const submittedOrder = question.submitted_answers || [];
    const correctOrder = question.correct_answers_ids || [];
    return (
        <>
            <div className="result_ordering_list">
                {submittedOrder.map((id, i) => {
                    const answer = (question.answers || []).find((a) => a.id === id);
                    const isRight = id === correctOrder[i];
                    const expected = !isRight ? (question.answers || []).find((a) => a.id === correctOrder[i]) : null;
                    return (
                        <div key={id} className={`result_ordering_item ${isRight ? "correct" : "wrong"}`}>
                            <span className="result_ordering_pos">{i + 1}</span>
                            {answer?.text}
                            {!isRight && expected &&
                                <span className="result_ordering_expected">Должно быть: {expected.text}</span>
                            }
                        </div>
                    );
                })}
            </div>
            {!question.correct &&
                <div className="result_ordering_correct_row">
                    <div className="result_ordering_correct_label">Правильный порядок:</div>
                    {correctOrder.map((id, i) => {
                        const a = (question.answers || []).find((x) => x.id === id);
                        return (
                            <span key={id} className="result_ordering_correct_chip">
                                {i + 1}. {a?.text}
                            </span>
                        );
                    })}
                </div>
            }
        </>
    );
};

// Установление пар. В отличие от прохождения теста учеником, здесь недоступна
// информация о том, с чем именно студент ошибочно сопоставил левый элемент -
// сервер хранит только ID верно сопоставленных строк (submitted_answers), сама
// попытка неверной пары нигде не сохраняется. Поэтому неверные строки помечаем
// как неверные и показываем эталонную пару, но не "что выбрал студент".
const renderMatchingResult = (question, onImageClick) => {
    const rightIds = question.submitted_answers || [];
    return (
        <div className="result_matching_list">
            {(question.answers || []).map((row) => {
                const isRight = rightIds.includes(row.id);
                return (
                    <div key={row.id} className={`result_matching_item ${isRight ? "correct" : "wrong"}`}>
                        <div className="result_matching_pair">
                            <span>{row.text}</span>
                            <span className="result_matching_arrow">→</span>
                            <span>{row.pair_item}</span>
                            <span className="result_icon">
                                {isRight ? "✔" : "✕"}
                            </span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const renderResultCard = (question, ind, onImageClick) => {
    const type = question.type || "single";
    let body;
    if (type === "ordering") body = renderOrderingResult(question, onImageClick);
    else if (type === "matching") body = renderMatchingResult(question, onImageClick);
    else body = renderChoiceResult(question, onImageClick);
    return (
        <div key={question.id} className={`quiz_result_card ${question.correct ? "correct" : "incorrect"}`}>
            <div className="quiz_result_card_title">{ind + 1}. {question.title}</div>
            {body}
        </div>
    );
};

const CheckTest = () => {
    const variable_1 = variableLanguage({keyPage: "checkTestWork", keyVariable: "variable_1"});
    const variable_2 = variableLanguage({keyPage: "checkTestWork", keyVariable: "variable_2"});
    const variable_3 = variableLanguage({keyPage: "checkTestWork", keyVariable: "variable_3"});
    const variable_4 = variableLanguage({keyPage: "checkTestWork", keyVariable: "variable_4"});
    const variable_5 = variableLanguage({keyPage: "checkTestWork", keyVariable: "variable_5"});
    const variable_6 = variableLanguage({keyPage: "checkTestWork", keyVariable: "variable_6"});
    const variable_7 = variableLanguage({keyPage: "checkTestWork", keyVariable: "variable_7"});
    const variable_11 = variableLanguage({keyPage: "checkTestWork", keyVariable: "variable_11"});

    const params = useParams();
    const history = useNavigate();
    const [quizData, setQuizData] = useState({});
    const [lightboxSrc, setLightboxSrc] = useState(null);

    useEffect(() => {
        requests.quiz.get_quiz_result_id(params)
            .then((res) => {
                setQuizData(res.data);
            })
            .catch(err => console.error(err));
    }, []);
    return (
        <div className={"check_test"} >
            <div
                className="group_demo-block"
                style={{display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10}}
            >
                <PrevBtn click={() => history(-1)}/>
                <DemoRow/>
            </div>
            <div className="top_info">
                <div className="image_block">
                    <img src={quizData.user && quizData.user.profile.avatar || fake_photos}/>
                    <div className="fio">{quizData.user && quizData.user.profile.name || ""}</div>
                </div>
                <div className={"info_row"}>
                    <span>{variable_11} </span>
                    <span>{quizData.attempts ? variable_1 : variable_2}</span>
                </div>
                <div className={"info_row"}>
                    <span>{variable_3} </span>
                    <span>{quizData.stage && quizData.stage.quest.name || ""}</span>
                </div>
                <div className={"info_row"}>
                    <span>{variable_4}: </span>
                    <span> {quizData.stage && quizData.stage.name}</span>
                </div>
                <div className={"info_row"}>
                    <span>{variable_5} </span>
                    <span> {quizData.title && quizData.title || ""}</span>
                </div>
                {quizData.attempts &&
                    <div className={"info_row"}>
                        <span>{variable_6}  </span>
                        <span> {quizData.results && quizData.results.length > 0 ? quizData.results.length : 0} </span>
                        <span style={{color: "#757A88", marginLeft: 3}}> / </span>
                        <span style={{color: "#757A88", marginLeft: 3}}> {quizData.attempts} </span>
                    </div>
                }

            </div>
            <div className="list_history-quiz">
                {quizData?.results?.length > 0 &&
                    quizData.results.map((elem, indx) =>
                        <div className="history_quiz control_quiz" key={indx}>
                            <div className="top_block">
                                <div className="group_left">
                                    {elem.correct_answers_count !== elem.questions_count
                                        ? <Deny/>
                                        : <Accept/>
                                    }
                                    <div className={"group_block"}>
                                        <span>
                                            Попытка №{elem.attempt_number && elem.attempt_number}
                                        </span>
                                        <div style={{height: 22, margin: "10px 5px 0 5px"}}>
                                            <span style={{color: "#757A88"}}>
                                                ({elem.correct_answers_count || 0}
                                            </span>
                                            <span style={{color: "#757A88", marginLeft: 3}}>
                                                /
                                            </span>
                                            <span style={{color: "#757A88", marginLeft: 3}}>
                                                {elem.questions_count && elem.questions_count || ""})
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className={"date_current"}>
                                    <span style={{color: "#757A88"}}>
                                        {variable_7}
                                    </span>
                                    <span>
                                        {elem.created_at && moment(elem.created_at).format("DD.MM.YY / HH:mm:ss") || ""}
                                    </span>
                                </div>
                            </div>
                            <div className="quiz_result_cards">
                                {elem.questions && elem.questions.length > 0 &&
                                    elem.questions.map((question, ind) =>
                                        renderResultCard(question, ind, (src) => setLightboxSrc(src)))
                                }
                            </div>
                        </div>
                    )
                }
            </div>
            <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)}/>
        </div>
    );
};

export default CheckTest;